import React, {useContext, useMemo} from 'react';
import '../../styles/SeccioMenuCards.css';
import {useNavigate} from "react-router-dom";
import {AuthContext} from "../../config/AuthContext";
import {
    BiListUl, BiFoodMenu, BiRestaurant, BiCart, BiPackage, BiGroup,
    BiBuildings, BiClipboard, BiEnvelope, BiReceipt, BiIdCard, BiSpreadsheet,
    BiUser, BiCog, BiWindows, BiHomeAlt, BiFirstAid, BiTime, BiPackage as BiBundle
} from "react-icons/bi";
import {IconType} from "react-icons";

interface MenuChild {
    key: string;
    label: string;
    children?: MenuChild[];
}

interface SeccioMenuCardsProps {
    pantalla: string;   // nom de la portada (ex: "menudesign", "artsiesports")
    titol: string;      // títol a mostrar
    subtitol?: string;
    // "page" (per defecte): capçalera gran centrada, com Ecoambit/Multilleure.
    // "group": el títol es mostra com un separador de grup (estil "COMANDES"),
    //          pensat per incrustar la secció dins d'una altra pàgina (ex: Home).
    variant?: "page" | "group";
}

// Assigna una icona segons paraules clau del label
function iconaPerLabel(label: string): IconType {
    const l = label.toLowerCase();

    // --- Opcions del menú "General" (root) ---
    // Es comproven primer perquè algunes contenen paraules ("escola", "alerg")
    // que coincidirien amb regles genèriques més avall.
    if (l.includes('usuari')) return BiUser;                          // Gestió Usuaris
    if (l.includes('control horari') || l.includes('horari')) return BiTime; // Control Horari
    if (l.includes('bundle')) return BiBundle;                        // Configuració Bundles
    if (l.includes('pàgin') || l.includes('pagin') || l.includes('pantall')) return BiWindows; // Configuració Pàgines
    if (l.includes('alerg') && l.includes('gesti')) return BiFirstAid; // Gestió Al·lergens
    if (l.includes('escola') && l.includes('gesti')) return BiHomeAlt; // Gestió Escoles
    if (l.includes('configuraci')) return BiCog;                      // Configuració web / genèric

    // --- Seccions (ecoambit, artiesport...) ---
    if (l.includes('ingredient')) return BiRestaurant;
    if (l.includes('plat')) return BiFoodMenu;
    if (l.includes('menu') || l.includes('menú')) return BiListUl;
    if (l.includes('comanda') || l.includes('proposta')) return BiCart;
    if (l.includes('stock')) return BiPackage;
    if (l.includes('proveïdor') || l.includes('proveidor')) return BiBuildings;
    if (l.includes('classe') || l.includes('escola')) return BiBuildings;
    if (l.includes('participant')) return BiGroup;
    if (l.includes('variant') || l.includes('alerg')) return BiClipboard;
    if (l.includes('email') || l.includes('e-mail')) return BiEnvelope;
    if (l.includes('rebut') || l.includes('remes') || l.includes('factur')) return BiReceipt;
    if (l.includes('beca') || l.includes('beque')) return BiSpreadsheet;
    if (l.includes('contacte') || l.includes('alumne')) return BiIdCard;
    if (l.includes('casal') || l.includes('inscripc') || l.includes('activitat')) return BiClipboard;
    return BiListUl;
}

const SeccioMenuCards: React.FC<SeccioMenuCardsProps> = ({pantalla, titol, subtitol, variant = "page"}) => {
    const navigate = useNavigate();
    const {user, setActualSite} = useContext(AuthContext);

    // Menú d'aquesta pantalla
    const menu: MenuChild[] = useMemo(() => {
        const mp = (user.menus || []).find((m: any) => m.pantalla === pantalla);
        return (mp?.menu as MenuChild[]) || [];
    }, [user.menus, pantalla]);

    // Separa items simples i grups (amb children). Els items simples han de
    // tenir una key de ruta vàlida (amb '/' o pàgina root); si no, són
    // contenidors sense fills visibles i s'ometen. Els grups buits també.
    const esRuta = (item: MenuChild) => !!item.key && (item.key.includes('/') || pantalla === 'root');
    const simples = menu.filter(item => (!item.children || item.children.length === 0) && esRuta(item));
    const grups = menu.filter(item => item.children && item.children.length > 0);

    function anar(key: string) {
        setActualSite(pantalla);
        navigate("/" + key.replace(/^\//, ''));
    }

    function Card({item}: {item: MenuChild}) {
        const Icona = iconaPerLabel(item.label);
        return (
            <button className="seccio-card" onClick={() => anar(item.key)}>
                <span className="seccio-card-icon"><Icona/></span>
                <span className="seccio-card-label">{item.label}</span>
            </button>
        );
    }

    // En variant "group" no fem servir el wrapper amb padding gran ni la
    // capçalera centrada: mostrem el títol com un separador de grup.
    const esGrup = variant === "group";

    return (
        <div className={esGrup ? "seccio-wrapper seccio-wrapper--group" : "seccio-wrapper"}>
            {esGrup ? (
                <div className="seccio-group-title seccio-group-title--main">{titol}</div>
            ) : (
                <div className="seccio-header">
                    <h2>{titol}</h2>
                    {subtitol && <div className="subtitle">{subtitol}</div>}
                </div>
            )}

            {menu.length === 0 ? (
                <div className="seccio-empty">No hi ha opcions disponibles.</div>
            ) : (
                <>
                    {/* Items simples primer, en graella */}
                    {simples.length > 0 && (
                        <div className="seccio-grid">
                            {simples.map(item => <Card key={item.key} item={item}/>)}
                        </div>
                    )}

                    {/* Grups amb els seus subelements */}
                    {grups.map(grup => (
                        <div className="seccio-group" key={grup.label}>
                            <div className="seccio-group-title">{grup.label}</div>
                            <div className="seccio-grid">
                                {grup.children!.map(child => <Card key={child.key} item={child}/>)}
                            </div>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
};

export default SeccioMenuCards;
